// mfa setup page - allows users to configure 2fa
import React, { useState } from "react";
import QRCode from "qrcode";
import {
  useSetupTotpMutation,
  useEnableTotpMutation,
  useDisableTotpMutation,
  useListPasskeysQuery,
  useDeletePasskeyMutation,
  useGetRecoveryCountQuery,
  useRegenerateRecoveryCodesMutation,
  useGetPasskeyRegistrationOptionsMutation,
  useVerifyPasskeyRegistrationMutation,
  useGetMeQuery,
} from "../../services/rtkApi/authServiceApi";
import type { AccountProof, StepUpProof } from "../../services/rtkApi/authServiceApi";
import { useAlert } from "../../ui/components/ConfirmDialog";
import StepUpCodeField, { stepUpErrorMessage } from "./StepUpCodeField";
import "./mfaSetup.css";

const MfaSetupPage: React.FC = () => {
  const alert = useAlert();
  // totp state
  const [totpStep, setTotpStep] = useState<"idle" | "setup" | "verify">("idle");
  const [totpSecret, setTotpSecret] = useState("");
  const [totpQrCode, setTotpQrCode] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [totpError, setTotpError] = useState("");

  // passkey state
  const [passkeyName, setPasskeyName] = useState("");
  const [passkeyError, setPasskeyError] = useState("");
  const [removingPasskeyId, setRemovingPasskeyId] = useState<string | null>(null);
  const [passkeyPassword, setPasskeyPassword] = useState("");
  const [passkeyProof, setPasskeyProof] = useState<StepUpProof | null>(null);

  // recovery codes state
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [recoveryPassword, setRecoveryPassword] = useState("");
  const [recoveryProof, setRecoveryProof] = useState<StepUpProof | null>(null);

  // disable state
  const [disablePassword, setDisablePassword] = useState("");
  const [disableProof, setDisableProof] = useState<StepUpProof | null>(null);
  const [disableError, setDisableError] = useState("");
  // a spent code stays in the field, so remount it after a success
  const [disableFieldKey, setDisableFieldKey] = useState(0);

  // accounts without a password confirm these with a code from their email.
  // step_up_required from auth also switches, in case /auth/me did not say
  const { data: me } = useGetMeQuery();
  const [stepUpRequired, setStepUpRequired] = useState(false);
  const needsCode = me?.hasPassword === false || stepUpRequired;
  const noteStepUpRequired = (err: any) => {
    if (err?.data?.code === "step_up_required") setStepUpRequired(true);
  };
  const proofFrom = (password: string, stepUp: StepUpProof | null): AccountProof | null => {
    if (needsCode) return stepUp;
    return password ? { password } : null;
  };

  // api hooks
  const [setupTotp] = useSetupTotpMutation();
  const [enableTotp, { isLoading: enablingTotp }] = useEnableTotpMutation();
  const [disableTotp, { isLoading: disablingTotp }] = useDisableTotpMutation();
  const { data: passkeys, refetch: refetchPasskeys } = useListPasskeysQuery();
  const [deletePasskey, { isLoading: deletingPasskey }] = useDeletePasskeyMutation();
  const { data: recoveryCount } = useGetRecoveryCountQuery();
  const [regenerateRecoveryCodes, { isLoading: regenerating }] = useRegenerateRecoveryCodesMutation();
  const [getPasskeyOptions] = useGetPasskeyRegistrationOptionsMutation();
  const [verifyPasskeyRegistration] = useVerifyPasskeyRegistrationMutation();

  // handle totp setup
  const handleSetupTotp = async () => {
    try {
      setTotpError("");
      const result = await setupTotp().unwrap();
      setTotpSecret(result.manualEntryKey);

      // generate qr code
      const qrDataUrl = await QRCode.toDataURL(result.qrCodeUri);
      setTotpQrCode(qrDataUrl);
      setTotpStep("setup");
    } catch (err: any) {
      setTotpError(err?.data?.error || "failed to setup totp");
    }
  };

  // handle totp enable
  const handleEnableTotp = async () => {
    try {
      setTotpError("");
      await enableTotp({ code: totpCode }).unwrap();
      setTotpStep("idle");
      setTotpCode("");
      setTotpSecret("");
      setTotpQrCode("");
    } catch (err: any) {
      setTotpError(err?.data?.error || "invalid code");
    }
  };

  // handle totp disable
  const handleDisableTotp = async () => {
    const proof = proofFrom(disablePassword, disableProof);
    if (!proof) return;
    try {
      setDisableError("");
      await disableTotp(proof).unwrap();
      setDisablePassword("");
      setDisableProof(null);
      setDisableFieldKey((key) => key + 1);
    } catch (err: any) {
      noteStepUpRequired(err);
      setDisableError(stepUpErrorMessage(err, "failed to disable totp"));
    }
  };

  // handle passkey registration
  const handleRegisterPasskey = async () => {
    try {
      setPasskeyError("");

      // get registration options
      const { options } = await getPasskeyOptions().unwrap();

      // create credential
      const credential = await navigator.credentials.create({
        publicKey: options,
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error("failed to create credential");
      }

      const response = credential.response as AuthenticatorAttestationResponse;

      // verify registration
      await verifyPasskeyRegistration({
        credential: {
          id: credential.id,
          rawId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))),
          type: credential.type,
          response: {
            clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(response.clientDataJSON))),
            attestationObject: btoa(String.fromCharCode(...new Uint8Array(response.attestationObject))),
            transports: response.getTransports?.() || [],
          },
        },
        deviceName: passkeyName || undefined,
      }).unwrap();

      setPasskeyName("");
      refetchPasskeys();
    } catch (err: any) {
      setPasskeyError(err?.data?.error || err?.message || "failed to register passkey");
    }
  };

  // passkey removal asks for proof inline: a code cannot be typed into prompt()
  const startPasskeyRemoval = (passkeyId: string) => {
    setRemovingPasskeyId(passkeyId);
    setPasskeyPassword("");
    setPasskeyProof(null);
  };

  const cancelPasskeyRemoval = () => {
    setRemovingPasskeyId(null);
    setPasskeyPassword("");
    setPasskeyProof(null);
  };

  // handle passkey delete
  const handleDeletePasskey = async (passkeyId: string) => {
    const proof = proofFrom(passkeyPassword, passkeyProof);
    if (!proof) return;

    try {
      await deletePasskey({ passkeyId, ...proof }).unwrap();
      cancelPasskeyRemoval();
    } catch (err: any) {
      noteStepUpRequired(err);
      await alert({ title: "Delete failed", message: stepUpErrorMessage(err, "failed to delete passkey") });
    }
  };

  // handle recovery codes regeneration
  const handleRegenerateRecoveryCodes = async () => {
    const proof = proofFrom(recoveryPassword, recoveryProof);
    if (!proof) return;
    try {
      const result = await regenerateRecoveryCodes(proof).unwrap();
      setRecoveryCodes(result.codes);
      setShowRecoveryCodes(true);
      setRecoveryPassword("");
      setRecoveryProof(null);
    } catch (err: any) {
      noteStepUpRequired(err);
      await alert({ title: "Regenerate failed", message: stepUpErrorMessage(err, "failed to regenerate codes") });
    }
  };

  return (
    <div className="mfa-setup-page">
      <h1>Two-Factor Authentication</h1>
      <p>Add an extra layer of security to your account</p>

      {/* totp section */}
      <section className="mfa-section">
        <h2>Authenticator App</h2>
        <p>Use an app like Google Authenticator or Authy</p>

        {totpStep === "idle" && (
          <button onClick={handleSetupTotp} className="mfa-setup-btn">
            Set Up Authenticator
          </button>
        )}

        {totpStep === "setup" && (
          <div className="totp-setup">
            <div className="qr-code">
              <img src={totpQrCode} alt="TOTP QR Code" />
            </div>
            <p className="manual-key">
              Manual entry key: <code>{totpSecret}</code>
            </p>
            <div className="verify-section">
              <input
                type="text"
                placeholder="Enter 6-digit code"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                maxLength={6}
              />
              <button
                onClick={handleEnableTotp}
                disabled={enablingTotp || totpCode.length !== 6}
              >
                {enablingTotp ? "Verifying..." : "Enable"}
              </button>
            </div>
            {totpError && <div className="error">{totpError}</div>}
          </div>
        )}
      </section>

      {/* passkeys section */}
      <section className="mfa-section">
        <h2>Passkeys</h2>
        <p>Use biometrics or security keys for passwordless login</p>

        <div className="passkey-list">
          {passkeys?.map((pk) => (
            <React.Fragment key={pk.id}>
              <div className="passkey-item">
                <div className="passkey-info">
                  <span className="passkey-name">{pk.deviceName}</span>
                  <span className="passkey-date">
                    Added {new Date(pk.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <button
                  onClick={() => startPasskeyRemoval(pk.id)}
                  className="delete-btn"
                >
                  Remove
                </button>
              </div>
              {removingPasskeyId === pk.id && (
                <div className="regenerate-section">
                  {needsCode ? (
                    <div style={{ flex: 1 }}>
                      <StepUpCodeField action="delete_passkey" onProofChange={setPasskeyProof} />
                    </div>
                  ) : (
                    <input
                      type="password"
                      placeholder="Enter your password to remove this passkey"
                      value={passkeyPassword}
                      onChange={(e) => setPasskeyPassword(e.target.value)}
                    />
                  )}
                  <button
                    onClick={() => handleDeletePasskey(pk.id)}
                    disabled={deletingPasskey || !proofFrom(passkeyPassword, passkeyProof)}
                    className="danger-btn"
                  >
                    {deletingPasskey ? "Removing..." : "Remove passkey"}
                  </button>
                  <button onClick={cancelPasskeyRemoval}>Cancel</button>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="add-passkey">
          <input
            type="text"
            placeholder="Device name (optional)"
            value={passkeyName}
            onChange={(e) => setPasskeyName(e.target.value)}
          />
          <button onClick={handleRegisterPasskey}>Add Passkey</button>
        </div>
        {passkeyError && <div className="error">{passkeyError}</div>}
      </section>

      {/* recovery codes section */}
      <section className="mfa-section">
        <h2>Recovery Codes</h2>
        <p>
          Use these codes to access your account if you lose your authenticator.
          You have <strong>{recoveryCount?.count || 0}</strong> codes remaining.
        </p>

        {showRecoveryCodes ? (
          <div className="recovery-codes">
            <p>Save these codes in a safe place. Each code can only be used once.</p>
            <div className="codes-grid">
              {recoveryCodes.map((code, i) => (
                <code key={i}>{code}</code>
              ))}
            </div>
            <button onClick={() => setShowRecoveryCodes(false)}>
              I've saved these codes
            </button>
          </div>
        ) : (
          <div className="regenerate-section">
            {needsCode ? (
              <div style={{ flex: 1 }}>
                <StepUpCodeField action="regenerate_recovery" onProofChange={setRecoveryProof} />
              </div>
            ) : (
              <input
                type="password"
                placeholder="Enter your password"
                value={recoveryPassword}
                onChange={(e) => setRecoveryPassword(e.target.value)}
              />
            )}
            <button
              onClick={handleRegenerateRecoveryCodes}
              disabled={regenerating || !proofFrom(recoveryPassword, recoveryProof)}
            >
              {regenerating ? "Generating..." : "Generate New Codes"}
            </button>
          </div>
        )}
      </section>

      {/* disable section */}
      <section className="mfa-section danger">
        <h2>Disable Two-Factor Authentication</h2>
        <p>Warning: This will remove all 2FA methods from your account.</p>
        <div className="disable-section">
          {needsCode ? (
            <div style={{ flex: 1 }}>
              <StepUpCodeField key={disableFieldKey} action="disable_totp" onProofChange={setDisableProof} />
            </div>
          ) : (
            <input
              type="password"
              placeholder="Enter your password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
            />
          )}
          <button
            onClick={handleDisableTotp}
            disabled={disablingTotp || !proofFrom(disablePassword, disableProof)}
            className="danger-btn"
          >
            {disablingTotp ? "Disabling..." : "Disable All 2FA"}
          </button>
        </div>
        {disableError && <div className="error">{disableError}</div>}
      </section>
    </div>
  );
};

export default MfaSetupPage;
